import React, { useEffect, useRef, useState } from "react";
import { api, formatEUR, hasPerm } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Download, Edit3, X, Layers, Sparkles, Barcode } from "lucide-react";
import { downloadFile } from "@/lib/download";
import { PRINT_STATIONS, stationLabel } from "@/lib/comanda";
import { useFeatures } from "@/lib/features";

const PALETTE = ["#1E2D5B", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#0A0A0A"];

const MODE_LABELS = {
  ristorante: "Ristorante", bar: "Bar/Tavola calda", pizzeria: "Pizzeria d'asporto",
  parrucchiere: "Parrucchiere", alimentari: "Alimentari/Market", macelleria: "Macelleria/Pescheria",
  panetteria: "Panetteria/Pasticceria", tabacchi: "Tabacchi", edicola: "Edicola",
  enoteca: "Enoteca", gelateria: "Gelateria", autolavaggio: "Autolavaggio",
  bnb: "B&B/Affittacamere", lavanderia: "Lavanderia", negozio: "Negozio",
  festa: "Feste del paese",
};

export default function Products() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editProd, setEditProd] = useState(null);
  const [activeMode, setActiveMode] = useState("all");
  const [tenantModes, setTenantModes] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const fileRef = useRef();
  const { hasFeature } = useFeatures();
  const showHandheld = hasFeature("palmare");

  const loadCats = () => api.get("/categories").then((r) => {
    setCats(Array.isArray(r.data) ? r.data : []);
    if (!selectedCat && Array.isArray(r.data) && r.data[0]) setSelectedCat(r.data[0].id);
  });
  const loadProds = () => api.get("/products").then((r) => setProds(Array.isArray(r.data) ? r.data : []));
  const loadPrinters = () => api.get("/printers").then((r) => setPrinters((Array.isArray(r.data) ? r.data : []).filter(p => p.role === "comanda")));

  useEffect(() => {
    loadCats(); loadProds(); loadPrinters();
    api.get("/tenant/me").then(r => setTenantModes(Array.isArray(r.data.modes) ? r.data.modes : []));
  }, []); // eslint-disable-line

  const seedMissing = async () => {
    if (!window.confirm("Carica dati demo per tutti i mode mancanti? (le categorie esistenti non verranno toccate)")) return;
    setSeeding(true);
    try {
      const r = await api.post("/tenant/seed-demo");
      toast.success(`Demo: +${r.data.created_cats} categorie, +${r.data.created_prods} articoli`);
      loadCats(); loadProds();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore seed");
    } finally { setSeeding(false); }
  };

  const saveCat = async (cat) => {
    try {
      if (cat.id) {
        await api.patch(`/categories/${cat.id}`, cat);
        toast.success("Categoria aggiornata");
      } else {
        const r = await api.post("/categories", cat);
        setSelectedCat(r.data.id);
        toast.success("Categoria creata");
      }
      setEditCat(null);
      loadCats();
    } catch { toast.error("Errore salvataggio"); }
  };

  const delCat = async (cid) => {
    if (!window.confirm("Eliminare categoria e tutti i suoi articoli?")) return;
    await api.delete(`/categories/${cid}`);
    if (selectedCat === cid) setSelectedCat(null);
    toast.success("Categoria eliminata");
    loadCats(); loadProds();
  };

  const saveProd = async (p) => {
    const raw = String(p.price ?? "").trim().replace(",", ".");
    const price = raw === "" ? NaN : Number(raw);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Inserisci il prezzo");
      return;
    }
    const body = { ...p, price };
    try {
      if (p.id) {
        await api.patch(`/products/${p.id}`, body);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/products", body);
        toast.success("Articolo creato");
      }
      setEditProd(null);
      loadProds();
    } catch { toast.error("Errore salvataggio"); }
  };

  const delProd = async (pid) => {
    if (!window.confirm("Eliminare articolo?")) return;
    await api.delete(`/products/${pid}`);
    loadProds();
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/products/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${r.data.categories_created} nuove categorie, ${r.data.products_created} articoli creati, ${r.data.products_updated || 0} aggiornati`);
      loadCats(); loadProds();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore import"); }
    e.target.value = "";
  };

  const filtered = selectedCat ? (Array.isArray(prods) ? prods : []).filter((p) => p.category_id === selectedCat) : prods;

  // Filtra categorie per mode attivo
  const visibleCats = activeMode === "all"
    ? cats
    : (Array.isArray(cats) ? cats : []).filter(c => (c.mode || "_global") === activeMode || !c.mode);

  // Lista dei mode disponibili dal tenant + "Globale"
  const availableModeTabs = ["all", ...tenantModes];

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Catalogo</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Articoli & Categorie</h1>
          <p className="text-sm text-velta-mute mt-1">Gestisci il listino per ciascuna modalità POS — filtra qui sotto per organizzarli.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="products-seed-demo-btn" onClick={seedMissing} disabled={seeding} className="h-11 px-4 bg-[#2F9E77] hover:bg-[#1bb070] disabled:opacity-60 text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Sparkles className="w-4 h-4" /> {seeding ? "Carico…" : "Carica dati demo"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />
          <button data-testid="products-import-btn" onClick={() => fileRef.current?.click()} className="h-11 px-4 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Upload className="w-4 h-4" /> Importa Excel
          </button>
          <button
            data-testid="products-export-btn"
            onClick={async () => {
              try { await downloadFile("/products/export-excel", "articoli_velta.xlsx"); toast.success("Export avviato"); }
              catch { toast.error("Errore export"); }
            }}
            className="h-11 px-4 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium flex items-center gap-2 btn-tactile"
          >
            <Download className="w-4 h-4" /> Esporta Excel
          </button>
          <button
            data-testid="products-template-btn"
            onClick={async () => {
              try { await downloadFile("/products/export-excel?template=1", "template_articoli_velta.xlsx"); toast.success("Template scaricato"); }
              catch { toast.error("Errore download"); }
            }}
            title="Scarica un Excel vuoto con le sole intestazioni, pronto da compilare"
            className="h-11 px-4 bg-white border border-dashed border-velta-ink text-velta-ink hover:bg-velta-bg rounded-md font-medium flex items-center gap-2 btn-tactile"
          >
            <Download className="w-4 h-4" /> Template vuoto
          </button>
          <button data-testid="products-new-cat-btn" onClick={() => setEditCat({ name: "", color: PALETTE[cats.length % PALETTE.length], order: cats.length, visible: true, is_available_on_handheld: true, mode: activeMode !== "all" ? activeMode : null })} className="h-11 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
            <Plus className="w-4 h-4" /> Nuova categoria
          </button>
        </div>
      </div>

      {/* Mode tabs */}
      {tenantModes.length > 1 && (
        <div className="bg-white border border-velta-border rounded-md p-2 mb-4 flex items-center gap-1 overflow-auto pos-scroll">
          <Layers className="w-4 h-4 text-velta-mute mx-2 shrink-0" />
          {(Array.isArray(availableModeTabs) ? availableModeTabs : []).map(m => {
            const count = m === "all"
              ? cats.length
              : (Array.isArray(cats) ? cats : []).filter(c => (c.mode || null) === (m === "_global" ? null : m)).length;
            const label = m === "all" ? "Tutte" : (MODE_LABELS[m] || m);
            return (
              <button
                key={m}
                data-testid={`products-mode-tab-${m}`}
                onClick={() => { setActiveMode(m); setSelectedCat(null); }}
                className={`px-3 h-9 rounded-md text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${activeMode === m ? "bg-velta-ink text-white" : "text-velta-mute hover:text-velta-ink hover:bg-velta-bg"}`}
              >
                {label}
                <span className={`text-[10px] font-mono ${activeMode === m ? "text-white/70" : "text-velta-mute"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="text-xs text-velta-mute mb-4 font-mono">Formato Excel: colonne <strong>Nome (o Descrizione), Categoria, Prezzo (o Prezzo vendita), Prezzo acquisto, IVA, Codice a barre (o Codice prodotto), Colore, Stampante</strong></div>

      <div className="grid lg:grid-cols-12 gap-4">
        {/* Categories */}
        <div className="lg:col-span-4 bg-white border border-velta-border rounded-md p-4">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3 px-2">Categorie</div>
          <div className="space-y-1 max-h-[60vh] overflow-auto pos-scroll">
            {(Array.isArray(visibleCats) ? visibleCats : []).map((c) => (
              <div key={c.id} className={`group flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors ${selectedCat === c.id ? "bg-velta-ink text-white" : "hover:bg-velta-bg"}`} onClick={() => setSelectedCat(c.id)} data-testid={`cat-row-${c.id}`}>
                <div className="w-3 h-3 rounded-sm shrink-0" style={{ background: c.color }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  {c.mode && <div className={`text-[10px] font-mono uppercase tracking-wider ${selectedCat === c.id ? "text-white/60" : "text-velta-mute"}`}>{MODE_LABELS[c.mode] || c.mode}</div>}
                  {showHandheld && c.is_available_on_handheld === false && <div className={`text-[10px] ${selectedCat === c.id ? "text-white/70" : "text-velta-mute"}`}>Nascosta palmare</div>}
                </div>
                <div className={`text-xs font-mono ${selectedCat === c.id ? "text-white/60" : "text-velta-mute"}`}>{(Array.isArray(prods) ? prods : []).filter(p => p.category_id === c.id).length}</div>
                <button data-testid={`cat-edit-${c.id}`} onClick={(e) => { e.stopPropagation(); setEditCat(c); }} className={`${selectedCat === c.id ? "text-white hover:text-white/70" : "text-velta-mute hover:text-velta-ink"}`} title="Modifica categoria"><Edit3 className="w-4 h-4" /></button>
                <button data-testid={`cat-del-${c.id}`} onClick={(e) => { e.stopPropagation(); delCat(c.id); }} className={`${selectedCat === c.id ? "text-white hover:text-white/70" : "text-velta-error hover:text-velta-error/70"}`} title="Elimina categoria"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {visibleCats.length === 0 && <div className="p-6 text-center text-sm text-velta-mute">Nessuna categoria in questo mode. Clicca <strong>&quot;Carica dati demo&quot;</strong> o crea manualmente.</div>}
          </div>
        </div>

        {/* Products */}
        <div className="lg:col-span-8 bg-white border border-velta-border rounded-md p-4">
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Articoli</div>
            {selectedCat && (
              <button data-testid="products-new-btn" onClick={() => setEditProd({ name: "", category_id: selectedCat, price: "", vat: 22, show_price: true, visible: true, is_available_on_handheld: true, weighable: false, unit: "pz", duration_min: null })} className="text-sm font-medium text-[#1E2D5B] hover:underline flex items-center gap-1">
                <Plus className="w-4 h-4" /> Nuovo articolo
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-auto pos-scroll p-1">
            {(Array.isArray(filtered) ? filtered : []).map((p) => {
              const cat = cats.find((c) => c.id === p.category_id);
              const color = p.color || cat?.color || "#0A0A0A";
              return (
                <div key={p.id} className="border border-velta-border rounded-md overflow-hidden group">
                  <div className="h-2" style={{ background: color }} />
                  <div className="p-4">
                    <div className="font-medium mb-1">{p.name}</div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-lg font-bold">{formatEUR(p.price)}</span>
                      <span className="text-xs font-mono text-velta-mute">IVA {p.vat}%</span>
                    </div>
                    {p.effective_print_to && <div className="text-xs text-velta-mute mt-1 uppercase tracking-widest font-mono">→ {stationLabel(p.effective_print_to)}</div>}
                    {showHandheld && p.effective_handheld === false && <div className="text-[10px] text-velta-mute mt-0.5">Nascosto palmare</div>}
                    {p.department && <div className="text-[10px] text-velta-mute mt-0.5 uppercase tracking-widest font-mono">RT reparto {p.department}</div>}
                    <div className="flex gap-3 mt-3">
                      <button data-testid={`prod-edit-${p.id}`} onClick={() => setEditProd(p)} className="text-xs font-medium text-velta-ink hover:underline">Modifica</button>
                      <button data-testid={`prod-del-${p.id}`} onClick={() => delProd(p.id)} className="text-xs font-medium text-velta-error hover:underline">Elimina</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="col-span-2 p-12 text-center text-sm text-velta-mute">Nessun articolo in questa categoria.</div>}
          </div>
        </div>
      </div>

      {/* Modali */}
      {editCat && <CategoryModal cat={editCat} onClose={() => setEditCat(null)} onSave={saveCat} />}
      {editProd && <ProductModal prod={editProd} cats={cats} printers={printers} onClose={() => setEditProd(null)} onSave={saveProd} />}
    </div>
  );
}

function CategoryModal({ cat, onClose, onSave }) {
  const { hasFeature } = useFeatures();
  const showHandheld = hasFeature("palmare");
  const [f, setF] = useState(cat);
  const [tenantModes, setTenantModes] = useState([]);
  const [printers, setPrinters] = useState([]);
  useEffect(() => {
    api.get("/tenant/me").then(r => setTenantModes(Array.isArray(r.data.modes) ? r.data.modes : []));
    api.get("/printers").then(r => setPrinters((Array.isArray(r.data) ? r.data : []).filter(p => p.role === "comanda")));
  }, []);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-velta-border">
          <h3 className="font-heading text-xl font-semibold">{f.id ? "Modifica categoria" : "Nuova categoria"}</h3>
          <button data-testid="cat-modal-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome</label>
            <input data-testid="cat-modal-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Modalità POS</label>
            <select data-testid="cat-modal-mode" value={f.mode || ""} onChange={(e) => setF({ ...f, mode: e.target.value || null })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
              <option value="">— Globale (tutti i mode) —</option>
              {(Array.isArray(tenantModes) ? tenantModes : []).map(m => <option key={m} value={m}>{MODE_LABELS[m] || m}</option>)}
            </select>
          </div>
          {showHandheld && (
          <label className="flex items-center justify-between gap-3 bg-velta-bg border border-velta-border rounded-md px-3 h-12" data-testid="cat-modal-handheld">
              <span className="text-sm">Mostra su Palmare Sala</span>
              <input
                type="checkbox"
                checked={f.is_available_on_handheld !== false}
                onChange={(e) => setF({ ...f, is_available_on_handheld: e.target.checked })}
                className="w-4 h-4 accent-[#1E2D5B]"
              />
            </label>
          )}
          <div className="border-t border-velta-border pt-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-velta-mute mb-2">Default per articoli in questa categoria</div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Reparto Stampa Comanda</label>
            <select
              data-testid="cat-modal-default-printto"
              value={f.print_station || f.default_print_to || ""}
              onChange={(e) => {
                const v = e.target.value || null;
                setF({ ...f, default_print_to: v, print_station: v });
              }}
              className="h-12 w-full bg-white border border-velta-border rounded-md px-3 mb-3 outline-none focus:border-[#1E2D5B]"
            >
              <option value="">— Nessuna (nessuna comanda per default) —</option>
              {PRINT_STATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              {(Array.isArray(printers) ? printers : []).filter((p) => !PRINT_STATIONS.some((s) => s.id === p.name || s.aliases.includes(String(p.name || "").toLowerCase()))).map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Reparto stampante fiscale (default)</label>
            <select
              data-testid="cat-modal-default-department"
              value={f.default_department || ""}
              onChange={(e) => setF({ ...f, default_department: e.target.value || null })}
              className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            >
              <option value="">— Auto (in base a IVA) —</option>
              <option value="1">Reparto 1</option>
              <option value="2">Reparto 2</option>
              <option value="3">Reparto 3</option>
              <option value="4">Reparto 4</option>
              <option value="5">Reparto 5</option>
              <option value="6">Reparto 6</option>
              <option value="7">Reparto 7</option>
              <option value="8">Reparto 8</option>
            </select>
            <p className="text-[11px] text-velta-mute mt-2">
              Ogni articolo di questa categoria erediterà queste impostazioni se non ne ha di proprie. Es. Primi → <strong>Cucina</strong>, pizze → <strong>Pizzeria</strong>, bevande → <strong>Bar</strong>.
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Colore</label>
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(PALETTE) ? PALETTE : []).map((c) => (
                <button key={c} onClick={() => setF({ ...f, color: c })} className={`w-10 h-10 rounded-md border-2 ${f.color === c ? "border-velta-ink" : "border-transparent"}`} style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-velta-border bg-white flex gap-2">
          <button data-testid="cat-modal-cancel" onClick={onClose} className="h-12 flex-1 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium btn-tactile">Annulla</button>
          <button data-testid="cat-modal-save" onClick={() => onSave(f)} className="h-12 flex-1 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva</button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ prod, cats, printers = [], onClose, onSave }) {
  const { hasFeature } = useFeatures();
  const showHandheld = hasFeature("palmare");
  const [f, setF] = useState({ ...prod });
  const currentCat = cats.find((c) => c.id === f.category_id);
  const catDefaultPrintTo = currentCat?.print_station || currentCat?.default_print_to;
  const catDefaultDept = currentCat?.default_department;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-velta-border">
          <h3 className="font-heading text-xl font-semibold">{f.id ? "Modifica articolo" : "Nuovo articolo"}</h3>
          <button data-testid="prod-modal-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome *</label>
            <input data-testid="prod-modal-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Prezzo €</label>
              <input
                data-testid="prod-modal-price"
                type="text"
                inputMode="decimal"
                value={f.price === 0 || f.price ? String(f.price) : ""}
                onChange={(e) => {
                  const raw = e.target.value.replace(",", ".");
                  if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setF({ ...f, price: raw });
                }}
                placeholder="Es. 12,50"
                className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">IVA %</label>
              <select value={f.vat} onChange={(e) => setF({ ...f, vat: parseFloat(e.target.value) })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]">
                <option value={4}>4%</option><option value={5}>5%</option><option value={10}>10%</option><option value={22}>22%</option>
              </select>
            </div>
          </div>
          {hasPerm("can_view_purchase_prices") && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Prezzo di acquisto € (riservato)</label>
              <input
                data-testid="prod-modal-purchase"
                type="number"
                step="0.01"
                value={f.purchase_price ?? ""}
                onChange={(e) => setF({ ...f, purchase_price: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })}
                placeholder="Visibile solo a titolare e responsabile"
                className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2 flex items-center gap-1.5"><Barcode className="w-3.5 h-3.5" /> Codice a barre</label>
            <input
              data-testid="prod-modal-barcode"
              value={f.barcode || ""}
              onChange={(e) => setF({ ...f, barcode: e.target.value.trim() })}
              placeholder="Scansiona o digita EAN/UPC…"
              className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
            />
            <p className="text-[11px] text-velta-mute mt-1">Con il cursore in questo campo puoi usare direttamente il lettore. In cassa la scansione aggiunge l&apos;articolo al conto.</p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Categoria</label>
            <select data-testid="prod-modal-category" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
              {(Array.isArray(cats) ? cats : []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {(catDefaultPrintTo || catDefaultDept) && (
              <p className="text-[11px] text-velta-mute mt-1">
                Categoria &quot;{currentCat?.name}&quot; ha default:{" "}
                {catDefaultPrintTo && <>comanda → <strong>{catDefaultPrintTo}</strong></>}
                {catDefaultPrintTo && catDefaultDept && " · "}
                {catDefaultDept && <>reparto RT <strong>{catDefaultDept}</strong></>}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Reparto stampante fiscale (RT)</label>
            <select
              data-testid="prod-modal-department"
              value={f.department || ""}
              onChange={(e) => setF({ ...f, department: e.target.value || null })}
              className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
            >
              <option value="">{catDefaultDept ? `— Eredita da categoria (Reparto ${catDefaultDept}) —` : "— Auto (in base a IVA) —"}</option>
              <option value="1">Reparto 1 (default IVA 22%)</option>
              <option value="2">Reparto 2 (default IVA 10%)</option>
              <option value="3">Reparto 3 (default IVA 4%)</option>
              <option value="4">Reparto 4</option>
              <option value="5">Reparto 5</option>
              <option value="6">Reparto 6</option>
              <option value="7">Reparto 7</option>
              <option value="8">Reparto 8</option>
            </select>
            <p className="text-[11px] text-velta-mute mt-1">
              Reparto della stampante fiscale (Epson/RCH) su cui viene battuto l&apos;articolo. Serve per scorporare l&apos;IVA in Z-report.
            </p>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Reparto Stampa Comanda</label>
            <select data-testid="prod-modal-printto" value={f.print_station || f.print_to || ""} onChange={(e) => {
              const v = e.target.value || null;
              setF({ ...f, print_to: v, print_station: v });
            }} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
              <option value="">{catDefaultPrintTo ? `— Eredita da categoria (${stationLabel(catDefaultPrintTo)}) —` : "— Nessuna comanda —"}</option>
              {PRINT_STATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              {(Array.isArray(printers) ? printers : []).filter((p) => !PRINT_STATIONS.some((s) => s.id === p.name)).map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
            {printers.length === 0 && (
              <p className="text-xs text-velta-mute mt-2">
                Associa in <strong>Stampanti</strong> una termica IP al reparto (Cucina, Pizzeria, Bar).
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Colore (override)</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setF({ ...f, color: null })} className={`px-3 h-9 border-2 rounded-md text-xs ${!f.color ? "border-velta-ink" : "border-velta-border"}`}>Eredita da categoria</button>
              {(Array.isArray(PALETTE) ? PALETTE : []).map((c) => (
                <button key={c} onClick={() => setF({ ...f, color: c })} className={`w-9 h-9 rounded-md border-2 ${f.color === c ? "border-velta-ink" : "border-transparent"}`} style={{ background: c }} />
              ))}
            </div>
          </div>
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.show_price} onChange={(e) => setF({ ...f, show_price: e.target.checked })} /> Mostra prezzo</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.visible} onChange={(e) => setF({ ...f, visible: e.target.checked })} /> Visibile</label>
            {showHandheld && <label className="flex items-center gap-2 text-sm" data-testid="prod-modal-handheld"><input type="checkbox" checked={f.is_available_on_handheld !== false} onChange={(e) => setF({ ...f, is_available_on_handheld: e.target.checked })} /> Mostra su Palmare Sala</label>}
            <label className="flex items-center gap-2 text-sm" data-testid="prod-modal-weighable-wrap"><input data-testid="prod-modal-weighable" type="checkbox" checked={!!f.weighable} onChange={(e) => setF({ ...f, weighable: e.target.checked, unit: e.target.checked ? "kg" : "pz" })} /> A peso (€/kg)</label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Soglia scorta (alert)</label>
              <input data-testid="prod-modal-stock-limit" type="number" min="0" value={f.stock_limit ?? ""} onChange={(e) => setF({ ...f, stock_limit: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="vuoto = nessun avviso" className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              <p className="text-[11px] text-velta-mute mt-1">Si può sempre togliere: svuota il campo e salva. Non è bloccata.</p>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Scorta attuale</label>
              <input type="number" value={f.stock_current ?? ""} onChange={(e) => setF({ ...f, stock_current: e.target.value ? parseInt(e.target.value) : null })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Durata servizio (min) — usata dal calendario appuntamenti</label>
            <input data-testid="prod-modal-duration" type="number" step="5" placeholder="es. 30" value={f.duration_min ?? ""} onChange={(e) => setF({ ...f, duration_min: e.target.value ? parseInt(e.target.value) : null })} className="h-12 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          </div>
        </div>
        <div className="p-4 border-t border-velta-border bg-white flex gap-2">
          <button data-testid="prod-modal-cancel" onClick={onClose} className="h-12 flex-1 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium btn-tactile">Annulla</button>
          <button data-testid="prod-modal-save" onClick={() => onSave(f)} className="h-12 flex-1 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva articolo</button>
        </div>
      </div>
    </div>
  );
}
